import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { normalizeGuestEmail, validGuestEmail } from "@/lib/guestTicketProfile";

export type BuyerEmailRecognitionStatus = "idle" | "checking" | "member" | "guest" | "unknown";
export type BuyerEmailRecognition = { status: BuyerEmailRecognitionStatus; signInRequired: boolean };

export function useBuyerEmailRecognition(value: string, enabled = true): BuyerEmailRecognition {
  const checkEmail = trpc.auth.checkEmailAvailability.useMutation();
  const [recognition, setRecognition] = useState<BuyerEmailRecognition>({ status: "idle", signInRequired: false });
  const requestId = useRef(0);
  const normalized = normalizeGuestEmail(value);

  useEffect(() => {
    const request = ++requestId.current;
    if (!enabled || !validGuestEmail(normalized)) {
      setRecognition({ status: "idle", signInRequired: false });
      return;
    }
    setRecognition({ status: "checking", signInRequired: false });
    const timer = setTimeout(() => {
      checkEmail.mutateAsync({ email: normalized, purpose: "ticket_purchase" }).then(result => {
        if (requestId.current !== request) return;
        setRecognition({
          status: result?.status === "taken" ? "member" : result?.status === "available" ? "guest" : "unknown",
          signInRequired: result?.status === "taken" && result?.signInRequired === true,
        });
      }).catch(() => {
        if (requestId.current === request) setRecognition({ status: "unknown", signInRequired: false });
      });
    }, 600);
    return () => {
      clearTimeout(timer);
      if (requestId.current === request) requestId.current++;
    };
  // React Query's mutation function is stable; keep this tied to the normalized address.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, normalized]);

  return recognition;
}
